import React, { useState, useRef, useEffect, memo } from 'react';

const SortDropDown = memo(({ 
  options = [], 
  text = "Select Option", 
  handleChange, 
  color = { primary: "#10c9c0" },
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(text);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option.display);
    setIsOpen(false);
    if (handleChange) {
      handleChange(option);
    }
  };

  const resetToDefault = () => {
    setSelectedOption(text);
    setIsOpen(false);
    if (handleChange) {
      handleChange({ display: text, value: text });
    }
  };

  const isDefaultSelected = selectedOption === text;

  return (
    <div 
      ref={dropdownRef} 
      className="relative inline-block text-left min-w-[140px] sm:min-w-[160px]"
    >
      {/* Main Dropdown Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          relative w-full justify-between items-center px-3 sm:px-4 py-2 sm:py-2.5 
          text-sm sm:text-base font-medium rounded-lg border transition-all duration-300 
          focus:outline-none focus:ring-2 focus:ring-offset-1 transform hover:scale-[1.02]
          ${disabled 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
            : isDefaultSelected
              ? 'bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              : 'text-white border-transparent shadow-md hover:shadow-lg'
          }
          ${!disabled && !isDefaultSelected ? 'hover:brightness-110' : ''}
          ${isOpen && !disabled ? 'ring-2 ring-offset-1 scale-[1.02]' : ''}
        `}
        style={{
          backgroundColor: !disabled && !isDefaultSelected ? color.primary : undefined,
          focusRingColor: color.primary,
          '--tw-ring-color': color.primary + '40'
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center justify-between w-full">
          <span className={`truncate ${!isDefaultSelected ? 'text-white' : ''}`}>
            {selectedOption}
          </span>
          
          {/* Clear button for non-default selections */}
          {!isDefaultSelected && !disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                resetToDefault();
              }}
              className="ml-2 p-1 rounded-full hover:bg-white/20 transition-colors duration-200"
              aria-label="Clear selection"
            >
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {/* Dropdown Arrow */}
          <svg 
            className={`w-4 h-4 ml-2 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : 'rotate-0'
            } ${!isDefaultSelected ? 'text-white' : 'text-gray-400'}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          
          {/* Menu */}
          <div className="absolute right-0 z-20 w-full sm:w-56 mt-2 origin-top-right bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden animate-[fadeIn_0.2s_ease-out]">
            <div className="py-1" role="listbox">
              {options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  className={`
                    w-full text-left px-4 py-2.5 text-sm transition-all duration-200 flex items-center justify-between
                    ${selectedOption === option.display 
                      ? 'text-white font-medium shadow-sm' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                  style={{
                    backgroundColor: selectedOption === option.display ? color.primary : undefined
                  }}
                  role="option"
                  aria-selected={selectedOption === option.display}
                >
                  <span>{option.display}</span>
                  {selectedOption === option.display && (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      
      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
});

SortDropDown.displayName = 'SortDropDown';

export default SortDropDown;